import { PITCH_INDEX_MAX, PITCH_INDEX_MIN } from "../constants";
import { getLanguage } from "../i18n";
import { effectivePitchIndex, kitPieceIndex, noteNameForPitchIndex, scaleNeighbor, PENTATONIC_OFFSETS } from "../audio";
import { kitLabelsForTimbre } from "../micStore";
import { PlacedObject, sampleRailCenterline } from "../objects";
import { allowedPitchClasses } from "../pitchScale";
import { AppContext } from "../appContext";
import { PanelLayout } from "./panelLayout";

interface PitchButtonsDeps {
  onPitch(pitchIndex: number): void;
  onPitchEnd(pitchIndex: number): void;
  isClaimLocked(id: string): boolean;
}

interface ButtonGroup {
  el: HTMLDivElement;
  note: HTMLSpanElement;
}

const BELOW_OFFSET_PX = 26;
const STACK_GAP_PX = 30;
const STACK_OVERLAP_PX = 56;

/**
 * The ◀ note ▶ pitch overlay that follows the focused pitched body's screen
 * projection: the sole pitch input across both modes. A box/sphere/funnel shows
 * one group editing `pitchIndex`; a rail shows two — one under each endpoint,
 * editing `pitchIndex` (start) and `pitchIndexEnd` (end). Normal mode steps a
 * semitone clamped to the slider range; simple mode steps the pentatonic window
 * (`pentatonicNeighbor`). A kit body (drum / mic) cycles its piece list
 * instead, showing the piece label. An empty-kit mic, a muted or silent
 * (volume 0) body, and unfocused/empty selections hide it. Only the buttons take pointer events so the canvas beneath
 * stays clickable. Returns `update` for the render loop.
 */
export function initPitchButtons(ctx: AppContext, panel: PanelLayout, deps: PitchButtonsDeps): { update: () => void } {
  const { field, graph, settings } = ctx;

  /**
   * The piece labels of a kit-style body (drum / mic), or null for a
   * pitched (melodic) body or a non-shape entity. A non-null result switches the
   * overlay from note stepping to piece cycling.
   */
  function kitLabelsOf(obj: PlacedObject): readonly string[] | null {
    if (obj.type !== "box" && obj.type !== "sphere" && obj.type !== "funnel") {
      return null;
    }
    return kitLabelsForTimbre(obj.timbreId);
  }

  /**
   * Steps `cur` one step for the focused body. A kit body cycles its piece list
   * (wrapping); otherwise one degree of the allowed pitch-class set (simple mode →
   * pentatonic, else the wired PitchQuantize mask, else chromatic). Rail bodies
   * sound a separate timbre catalog no PitchQuantize governs, so they only follow
   * simple-mode pentatonic and are otherwise chromatic.
   */
  function stepValue(cur: number, dir: 1 | -1, obj: PlacedObject): number {
    const pieces = kitLabelsOf(obj);
    if (pieces !== null && pieces.length > 0) {
      return kitPieceIndex(cur + dir, pieces.length);
    }
    const classes = obj.type === "rail"
      ? (settings.simpleMode ? PENTATONIC_OFFSETS : null)
      : allowedPitchClasses(obj.timbreId, settings.simpleMode, field, graph);
    if (classes === null) {
      return Math.min(PITCH_INDEX_MAX, Math.max(PITCH_INDEX_MIN, cur + dir));
    }
    return scaleNeighbor(cur, dir, classes);
  }

  function makeGroup(role: "start" | "end"): ButtonGroup {
    const el = document.createElement("div");
    el.className = "pitch-buttons";
    el.style.display = "none";
    const down = document.createElement("button");
    down.className = "pitch-btn";
    down.textContent = "◀";
    const note = document.createElement("span");
    note.className = "pitch-btn-note";
    const up = document.createElement("button");
    up.className = "pitch-btn";
    up.textContent = "▶";
    el.append(down, note, up);
    const step = (dir: 1 | -1): void => {
      const obj = field.selectedObject();
      if (!obj) {
        return;
      }
      const cur = role === "end" ? obj.pitchIndexEnd : obj.pitchIndex;
      const next = stepValue(cur, dir, obj);
      if (role === "end") {
        deps.onPitchEnd(next);
      } else {
        deps.onPitch(next);
      }
    };
    for (const btn of [down, up]) {
      btn.addEventListener("pointerdown", (ev: PointerEvent): void => ev.stopPropagation());
    }
    down.addEventListener("click", (): void => step(-1));
    up.addEventListener("click", (): void => step(1));
    document.body.appendChild(el);
    return { el, note };
  }

  const startGroup = makeGroup("start");
  const endGroup = makeGroup("end");

  function place(group: ButtonGroup, x: number, y: number): void {
    group.el.style.display = "flex";
    group.el.style.left = `${x}px`;
    group.el.style.top = `${y}px`;
  }

  function hideAll(): void {
    startGroup.el.style.display = "none";
    endGroup.el.style.display = "none";
  }

  function hasPitchOverlay(obj: PlacedObject): boolean {
    if (obj.type === "rail") {
      return true;
    }
    if (obj.type === "box" || obj.type === "sphere" || obj.type === "funnel") {
      const pieces = kitLabelsForTimbre(obj.timbreId);
      return pieces === null || pieces.length > 0;
    }
    return false;
  }

  function noteFor(stored: number, obj: PlacedObject, transpose: number): string {
    const notation = getLanguage() === "ja" ? "ja" : "en";
    return noteNameForPitchIndex(effectivePitchIndex(stored, obj.timbreId, transpose), true, notation);
  }

  /**
   * The glyph shown between the step buttons: a kit body shows its current piece
   * label (empty when the kit has no pieces yet, e.g. an empty mic), every other
   * body its note name.
   */
  function glyphFor(obj: PlacedObject, stored: number, transpose: number): string {
    const pieces = kitLabelsOf(obj);
    if (pieces !== null) {
      return pieces.length > 0 ? pieces[kitPieceIndex(stored, pieces.length)] ?? "?" : "";
    }
    return noteFor(stored, obj, transpose);
  }

  function updateRail(obj: PlacedObject, transpose: number): void {
    const pos = field.liveOrBasePosition(obj.id);
    const quat = field.liveOrBaseRotation(obj.id);
    if (!pos || !quat || obj.railPoints.length < 2) {
      hideAll();
      return;
    }
    const center = sampleRailCenterline(obj.railPoints);
    const startWorld = center[0].clone().applyQuaternion(quat).add(pos);
    const endWorld = center[center.length - 1].clone().applyQuaternion(quat).add(pos);
    const ps = panel.projectToScreen(startWorld);
    const pe = panel.projectToScreen(endWorld);
    const startY = ps.y + BELOW_OFFSET_PX;
    let endY = pe.y + BELOW_OFFSET_PX;
    if (Math.hypot(ps.x - pe.x, startY - endY) < STACK_OVERLAP_PX) {
      endY = startY + STACK_GAP_PX;
    }
    startGroup.note.textContent = noteFor(obj.pitchIndex, obj, transpose);
    endGroup.note.textContent = noteFor(obj.pitchIndexEnd, obj, transpose);
    startGroup.note.style.visibility = "visible";
    endGroup.note.style.visibility = "visible";
    place(startGroup, ps.x, startY);
    place(endGroup, pe.x, endY);
  }

  function update(): void {
    const obj = field.selectedObject();
    if (!obj || !hasPitchOverlay(obj) || deps.isClaimLocked(obj.id) || obj.muted || obj.volume <= 0) {
      hideAll();
      return;
    }
    const transpose = obj.type === "rail" ? 0 : field.timbreTransposeFor(obj.timbreId);
    if (obj.type === "rail") {
      updateRail(obj, transpose);
      return;
    }
    const p = panel.projForNodeLive(obj.id);
    if (!p) {
      hideAll();
      return;
    }
    startGroup.note.textContent = glyphFor(obj, obj.pitchIndex, transpose);
    startGroup.note.style.visibility = kitLabelsOf(obj) !== null || !settings.showNoteName ? "visible" : "hidden";
    place(startGroup, p.x, p.y);
    endGroup.el.style.display = "none";
  }

  return { update };
}
